import { find, uniq, get, isPlainObject } from 'lodash-es'

export const useUser = defineStore('user', () => {
  const { $api, $cio, $newRelic, $faro } = useNuxtApp()

  const { getCookieParams } = useHelpers()

  const ui = useUi()

  const started = ref(false)

  const data = ref({})

  const token = ref(null)

  const isEditing = ref(false)

  const NEW_USER_THRESHOLD = 550
  const isNew = ref(false)

  const queue = useCookie('queue', {
    default: () => {
      return []
    }
  })

  const queuable = { castVote, saveMetadata }

  const voted = ref(false)

  const submittingVotes = ref([])

  const tokenCookie = useCookie('jwt', getCookieParams())

  const trackingCookie = useCookie('tracking')

  const partneroPartnerCookie = useCookie('partnero_partner')

  // Helper function to get friend/affiliate value
  const getFriendValue = () => {
    const route = useRoute()

    // First check query parameter
    if (route.query.friend) {
      return route.query.friend
    }

    // Then check Nuxt cookie
    if (partneroPartnerCookie.value) {
      return partneroPartnerCookie.value
    }

    return null
  }

  const isGuest = computed(() => !Object.keys(data.value).length)

  const name = computed(() => isGuest.value ? null : [data.value.first_name, data.value.last_name].join(' ').trim())

  const isProfileComplete = computed(() => {
    if (isGuest.value) return false
    return !!(data.value.first_name && data.value.first_name.trim() !== '' &&
              data.value.last_name && data.value.last_name.trim() !== '')
  })

  const completedExercises = computed(() => data.value.settings?.exercises || [])

  const hasTeamOrders = computed(() => {
    if (!data.value.orders) return false
    return data.value.orders.some(order => order.quantity > 1)
  })

  const canUpgrade = computed(() => {
    return data.value.orders?.some(order => order.can_upgrade)
  })

  const upgradeableOrders = computed(() => {
    return data.value.orders?.filter(order => order.can_upgrade)
  })

  const vouchersPurchased = computed(() => get(data.value, 'team_vouchers', []))

  const vouchersRedeemed = computed(() => get(data.value, 'vouchers', []))

  const hasJoinedDiscord = computed(() => {
    const discord = data.value?.socials?.find(social => social.name === 'discord')

    return discord?.joined || false
  })

  const teamMembers = useTeamMembers()
  const roles = computed(() => teamMembers.authMember?.roles || [])
  const isPartner = computed(() => roles.value?.includes('partner'))

  const postLoginTeamUrl = computed(() => isNew.value && ['partner', 'manager', 'team owner', 'team billing'].some(role => roles.value?.includes(role)) ? '/team/dashboard' : null)

  function start (payload) {
    // Write JWT cookie
    tokenCookie.value = payload.token

    // Clear tracking
    trackingCookie.value = null

    // Update store
    started.value = true
    data.value = payload.data
    token.value = payload.token
    console.info('Session started')

    if (process.client) {
      // Reset user related stores
      useCertificates().reset()
      useExams().reset()
      useOrders().reset()
      useProducts().reset()
      useSummaries().reset()
      useVouchers().reset()

      // Process queue
      processQueue()

      console.log('$newRelic.setUserId', data.value.email)
      $newRelic?.setUserId(data.value.email)
      $faro?.api?.setUser({ email: data.value.email })

      // Use friend value from backend payload (source of truth)
      const friendValue = data.value.friend

      // get the tech url from router and set it as
      // CIO identify
      $cio?.identify({
        id: data.value.email,
        created_at: Math.floor(new Date().getTime() / 1000),
        registered_from: data.value.metadata?.interest?.[0],
        first_name: data.value.first_name,
        last_name: data.value.last_name,
        email: data.value.email,
        interest: data.value.metadata?.interest,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(friendValue ? { friend: friendValue } : {})
      })
    }
  }

  function onboardingTeam () {
    if (!data.value?.onboarding?.onboarding_completed) {
      const firstTeam = data.value?.onboarding?.teams_onboarding?.find(team => !team.onboarding_completed)

      return firstTeam?.team_id
    }

    return null
  }

  function getPostLoginRedirectUrl (defaultRedirectUrl) {
    const teamsFeatureEnabled = useTeams().isFeatureEnabled
    const intendedUrl = useRoute().query.intended

    // Highest priority: query parameter intended URL
    if (intendedUrl) {
      return decodeURIComponent(intendedUrl)
    }

    // Second priority: onboarding team redirects (if applicable)
    if (data.value?.has_team_access && teamsFeatureEnabled) {
      const onboardingTeamId = onboardingTeam()
      if (onboardingTeamId) {
        return `/team/onboarding/${onboardingTeamId}`
      }
    }

    // Third priority: parameter provided (treat as intended redirect if not default)
    // This handles cases where callers pass intended redirects (voucher pages, tech dashboards, etc.)
    if (defaultRedirectUrl) {
      return decodeURIComponent(defaultRedirectUrl)
    }

    // Fourth priority: team dashboard (if user has team access)
    if (data.value?.has_team_access && teamsFeatureEnabled) {
      return '/team/dashboard'
    }

    // Fallback: default home page
    return '/'
  }

  function clear () {
    data.value = {}
    token.value = null
    tokenCookie.value = null
    console.log('$newRelic.setUserId', null)
    $newRelic?.setUserId(null)
    $faro?.api?.setUser(undefined)
    $cio?.reset()
  }

  const toggleEdit = () => {
    isEditing.value = !isEditing.value
  }

  function markAsNew () {
    isNew.value = true
  }

  async function login ({ email, password }) {
    const payload = await $api.post('/login', { email, password, ...trackingCookie.value })

    start(payload)

    const isNewUser = data.value.created_minutes_ago < NEW_USER_THRESHOLD
    if (isNewUser) markAsNew()
  }

  async function loginSocial ({ code, provider }) {
    const payload = await $api.post(`/login/${provider}/callback`, { code })

    // Inject friend value for Customer.io tracking
    const friendValue = getFriendValue()
    if (friendValue && payload.data) {
      payload.data.friend = friendValue
    }

    start(payload)

    const isNewUser = data.value.created_minutes_ago < NEW_USER_THRESHOLD
    if (isNewUser) markAsNew()
  }

  async function joinDiscord () {
    const { link } = await $api.post('/join/discord')

    return link
  }

  async function register ({ first_name, last_name, email, password, linkedin_achievement_share_permission }) {
    const payload = await $api.post('/register', {
      first_name,
      last_name,
      email,
      password,
      password_confirmation: password,
      linkedin_achievement_share_permission,
      ...trackingCookie.value
    })

    // Inject friend value for Customer.io tracking
    const friendValue = getFriendValue()
    if (friendValue && payload.data) {
      payload.data.friend = friendValue
    }

    // ui.displayFlashMessage()
    markAsNew()

    start(payload)
  }

  async function verifyEmail ({ id, hash, expires, signature }) {
    await $api.get(`/verify-email/${id}/${hash}`, {
      expires, signature
    })
  }

  async function impersonate (code) {
    const payload = await $api.post(`/impersonate/${code}`)

    clear()
    start(payload)
  }

  async function validate () {
    // Skip if the browser does not have a "jwt" cookie or the session has already started
    if (!tokenCookie.value || started.value) return

    try {
      const payload = await $api.get('/auth')
      start(payload)

      const isNewUser = data.value.created_minutes_ago < NEW_USER_THRESHOLD
      if (isNewUser) markAsNew()
    } catch (e) {
      clear()
    }
  }

  async function update ({ first_name, last_name, email, occupation, twitter, linkedin, linkedin_achievement_share_permission }) {
    const payload = await $api.post('/auth', { first_name, last_name, email, occupation, twitter, linkedin, linkedin_achievement_share_permission })

    data.value = payload.data
  }

  async function refreshUserData () {
    try {
      const payload = await $api.get('/auth')
      data.value = payload.data
    } catch (e) {
      console.error('Failed to refresh user data:', e)
    }
  }

  async function changePassword ({ current_password, new_password }) {
    await $api.put('/change-password', { current_password, new_password })
    clear()
  }

  function getPasswordRecoveryLink (email) {
    return $api.post('/forgot-password', { email })
  }

  async function resetPassword (params) {
    const payload = await $api.post('/reset-password', params)

    // Check if the response contains a token
    if (payload && payload.token) {
      // Set the token cookie first so the /auth request works
      tokenCookie.value = payload.token

      try {
        // Pass the token directly in the Authorization header to ensure it's used
        const userData = await $api.get('/auth', {}, {
          Authorization: `Bearer ${payload.token}`
        })
        const fullPayload = {
          token: payload.token,
          data: userData.data
        }
        start(fullPayload)
      } catch (e) {
        console.error('Failed to fetch user data after password reset:', e)
        // Still return the original payload even if user data fetch fails
      }
    }

    return payload
  }

  async function getPasswordResetFromInvitation (invitationToken) {
    return await $api.post('/auth/password-reset-from-invitation', {
      invitation_token: invitationToken
    })
  }

  async function logout ({ clearRedirection = true } = {}) {
    try {
      // Only call logout API if we have a valid token
      if (tokenCookie.value) {
        await $api.post('/logout')
      }
    } catch (e) {
      // Don't throw error, just log it and clear the session anyway
      console.error('Logout API call failed:', e)
    } finally {
      // Always clear session regardless of API success/failure
      clear()

      // Clear session skip flag on logout
      clearProfileCompletionSession()

      if (clearRedirection) {
        // Clear UI redirect URL to prevent immediate redirect to login with intended URL
        ui.redirectUrl = null
      }

      // Clear Team success cookie
      const modalClosed = useCookie('team-success-modal-closed')
      modalClosed.value = null
    }
  }

  async function storeSetting (payload) {
    data.value.settings = await $api.post('/user/settings', payload)
  }

  // Profile completion functions
  function checkProfileCompletion (redirectBack = true, intendedRoute = null) {
    // Only run on client side
    if (!process.client) return

    // Skip if user is guest
    if (isGuest.value) return

    // Skip if profile is already complete
    if (isProfileComplete.value) return

    // Check if user has skipped profile completion before
    const hasSkippedThisSession = sessionStorage.getItem('skipped-profile-completion-session')
    const hasSkippedPermanently = localStorage.getItem('skipped-profile-completion')

    if (hasSkippedThisSession || hasSkippedPermanently) {
      return
    }

    // Get current route for intended destination
    const route = useRoute()
    const router = useRouter()
    const targetPath = intendedRoute || route.fullPath || '/'
    console.log({
      targetPath
    })

    // Redirect to profile completion
    router.push(`/onboarding/profile${redirectBack ? `?intended=${encodeURIComponent(targetPath)}` : ''}`)

    return true
  }

  function setProfileCompletionSessionSkip () {
    if (process.client) {
      sessionStorage.setItem('skipped-profile-completion-session', 'true')
    }
  }

  function clearProfileCompletionSession () {
    if (process.client) {
      sessionStorage.removeItem('skipped-profile-completion-session')
    }
  }

  async function completeExercise (id) {
    let completed = data.value.settings?.exercises || []
    completed.push(id)
    completed = uniq(completed)
    return await storeSetting({ exercises: completed })
  }

  async function saveMetadata (payload) {
    try {
      await $api.post('/user/metadata', payload)
      data.value.metadata = isPlainObject(data.value.metadata) ? data.value.metadata : {}
      data.value.metadata.interest = data.value.metadata.interest || []
      data.value.metadata.interest.push(...payload.interest)
    } catch (e) {
      console.error('Error saving metadata', e)
    }
  }

  async function castVote ({ slug, votes }) {
    try {
      const votesIds = votes.map((vote) => vote.value)

      submittingVotes.value.push(...votesIds)

      await $api.post(`/polls/${slug}/vote`, { votes })

      voted.value = true
      submittingVotes.value = []

      data.value.metadata = isPlainObject(data.value.metadata) ? data.value.metadata : {}
      data.value.metadata.interest = data.value.metadata.interest || []
      data.value.metadata.interest.push(...votesIds)
    } catch (e) {
      console.error('Error casting vote', e)
    }
  }

  function addToQueue (params) {
    queue.value?.push(params)

    if (isGuest.value) return
    processQueue()
  }

  function processQueue () {
    console.info('Process queue...')

    queue.value?.forEach(async (item) => {
      try {
        await queuable[item.method](item.payload)
        if (item.flashMessageKey) {
          ui.flashMessageKey = item.flashMessageKey
          ui.displayFlashMessage()
        }
      } catch (e) {
        console.error(e)
        ui.showErrorModal(e)
      }
    })

    queue.value = []
  }

  function findVoucher (certificationId, productType, pool) {
    return find(pool, voucher => voucher?.product?.certification_id === certificationId && voucher?.product?.type === productType)
  }

  function accessTo (certificationId, productType) {
    if (findVoucher(certificationId, productType, vouchersRedeemed.value)) return 'REDEEMED'
    if (findVoucher(certificationId, productType, vouchersPurchased.value)) return 'PURCHASED'
    return null
  }

  return {
    started,
    start,
    data,
    token,
    isGuest,
    name,
    queue,
    addToQueue,
    voted,
    submittingVotes,
    login,
    loginSocial,
    joinDiscord,
    hasJoinedDiscord,
    register,
    impersonate,
    validate,
    update,
    refreshUserData,
    changePassword,
    getPasswordRecoveryLink,
    resetPassword,
    getPasswordResetFromInvitation,
    logout,
    storeSetting,
    checkProfileCompletion,
    setProfileCompletionSessionSkip,
    clearProfileCompletionSession,
    saveMetadata,
    completeExercise,
    completedExercises,
    castVote,
    accessTo,
    verifyEmail,
    vouchersPurchased,
    vouchersRedeemed,
    hasTeamOrders,
    canUpgrade,
    upgradeableOrders,
    findVoucher,
    processQueue,
    isEditing,
    toggleEdit,
    NEW_USER_THRESHOLD,
    isNew,
    markAsNew,
    roles,
    isPartner,
    postLoginTeamUrl,
    onboardingTeam,
    getPostLoginRedirectUrl,
    isProfileComplete
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUser, import.meta.hot))
}
